import { useId, useState } from 'react';
import { MAX_TEXTE_QUESTIONNAIRE, NIVEAUX_QUESTIONNAIRE } from '@neopolis/engine';
import type { NiveauQuestionnaire, Questionnaire } from '@neopolis/engine';

import { T } from '../textes-interface.js';

const LIBELLES: Record<NiveauQuestionnaire, string> = {
  peu: T.questionnaire.peu,
  moyen: T.questionnaire.moyen,
  bien: T.questionnaire.bien,
};

function TroisNiveaux({
  question,
  valeur,
  onChoisir,
}: {
  question: string;
  valeur: NiveauQuestionnaire | null;
  onChoisir: (niveau: NiveauQuestionnaire) => void;
}) {
  return (
    <fieldset>
      <legend className="font-semibold text-slate-800">{question}</legend>
      <div className="mt-2 flex flex-wrap gap-2">
        {NIVEAUX_QUESTIONNAIRE.map((niveau) => (
          <button
            key={niveau}
            type="button"
            aria-pressed={valeur === niveau}
            onClick={() => onChoisir(niveau)}
            className={`min-h-touch flex-1 rounded-xl border-2 px-4 font-semibold ${
              valeur === niveau
                ? 'border-sky-600 bg-sky-600 text-white'
                : 'border-slate-300 bg-white text-slate-700 hover:bg-slate-50'
            }`}
          >
            {LIBELLES[niveau]}
          </button>
        ))}
      </div>
    </fieldset>
  );
}

/**
 * Questionnaire de fin de chapitre, pour le pilote en classe.
 *
 * Trois questions, pas une de plus : les élèves y répondent en fin de séance,
 * quand la sonnerie approche. Le champ libre porte une consigne explicite de ne
 * pas écrire de prénom — c'est le seul endroit du jeu où un élève pourrait
 * laisser quelque chose d'identifiant.
 */
export function QuestionnaireDeFinDeChapitre({
  reponses,
  onRepondre,
}: {
  reponses: Questionnaire | undefined;
  onRepondre: (reponses: Questionnaire) => void;
}) {
  const [compris, setCompris] = useState<NiveauQuestionnaire | null>(reponses?.compris ?? null);
  const [aime, setAime] = useState<NiveauQuestionnaire | null>(reponses?.aime ?? null);
  const [difficile, setDifficile] = useState(reponses?.difficile ?? '');
  const [envoye, setEnvoye] = useState(reponses !== undefined);
  const idDifficile = useId();

  return (
    <section className="rounded-2xl border-2 border-sky-200 bg-white p-5 print:hidden">
      <h2 className="text-xl font-bold text-slate-900">{T.questionnaire.titre}</h2>

      <div className="mt-4 space-y-4">
        <TroisNiveaux
          question={T.questionnaire.compris}
          valeur={compris}
          onChoisir={(niveau) => {
            setCompris(niveau);
            setEnvoye(false);
          }}
        />

        <TroisNiveaux
          question={T.questionnaire.aime}
          valeur={aime}
          onChoisir={(niveau) => {
            setAime(niveau);
            setEnvoye(false);
          }}
        />

        <div>
          <label htmlFor={idDifficile} className="block font-semibold text-slate-800">
            {T.questionnaire.difficile}
          </label>
          <p className="text-sm text-slate-600">{T.questionnaire.difficileAide}</p>
          <input
            id={idDifficile}
            value={difficile}
            maxLength={MAX_TEXTE_QUESTIONNAIRE}
            onChange={(evenement) => {
              setDifficile(evenement.target.value);
              setEnvoye(false);
            }}
            className="mt-1 min-h-touch w-full rounded-xl border-2 border-slate-300 px-3 focus:border-sky-600"
          />
        </div>

        <button
          type="button"
          disabled={compris === null || aime === null}
          onClick={() => {
            if (compris === null || aime === null) return;
            onRepondre({ compris, aime, difficile });
            setEnvoye(true);
          }}
          className="min-h-touch w-full rounded-xl bg-sky-600 px-6 font-semibold text-white disabled:bg-slate-300 disabled:text-slate-500"
        >
          {T.questionnaire.envoyer}
        </button>

        {envoye && (
          <p aria-live="polite" className="font-semibold text-emerald-700">
            <span aria-hidden="true">✓ </span>
            {T.questionnaire.merci}
          </p>
        )}
      </div>
    </section>
  );
}
